import React from 'react'
import { Form, Button } from 'react-bootstrap'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { Box, LogoBox, ContentInput, MfaEmailNotice } from './styles'
import { useIsMounted } from './useIsMounted'
import type { MfaMethod } from '@/hooks/useAuth'

interface CodeValues {
  code: string
}

const codeSchema = Yup.object({
  code: Yup.string()
    /*
     * Descarta tudo que não é dígito antes de validar.
     *
     * No modo e-mail a pessoa copia o código da mensagem, e vem espaço ou
     * quebra de linha junto. Sem isto o cliente recusa um código correto com
     * "O código tem 6 dígitos", enquanto o servidor o aceitaria — ele já
     * normaliza do mesmo jeito (normalizeVerificationCode descarta não-dígitos).
     *
     * Vale também para o TOTP: ninguém digita espaço, então o comportamento de
     * quem digita não muda, e quem cola de um gerenciador de senhas deixa de
     * ser punido.
     */
    .transform(value =>
      typeof value === 'string' ? value.replace(/\D/g, '') : value
    )
    .matches(/^\d{6}$/, 'O código tem 6 dígitos')
    .required('Código obrigatório')
})

interface MfaVerifyStepProps {
  /**
   * Qual segundo fator a API escolheu para ESTE login, vindo do `method` da
   * resposta do `POST /sessions`. Muda só o texto da tela: o envio é o mesmo
   * `POST /sessions-mfa` com `{ challenge, code }` nos dois casos.
   */
  method: MfaMethod
  /**
   * E-mail de quem está entrando, para o modo e-mail dizer PARA ONDE o código
   * foi. Não vem da API: é o que a pessoa acabou de digitar (ou o `user.email`
   * da tela de inatividade), e a API de propósito não confirma e-mails.
   */
  email?: string
  /** Validade do código em minutos, só no modo e-mail (`timeout_minutes`). */
  timeoutMinutes?: number
  /** Envia o código. O erro é tratado por quem passou esta função. */
  onSubmit: (code: string) => Promise<void>
  /** Volta ao passo de e-mail/senha, descartando o challenge. */
  onBack: () => void
  /**
   * Tentativas restantes no challenge, do `attempts_left` da resposta de erro.
   *
   * Sem este consumo o campo seria descartado em silêncio: o interceptor só
   * exibe `data.message`, e o usuário levaria o challenge até zero sem aviso.
   */
  attemptsLeft?: number | null
  /** Penalidade de 429 em curso: o botão fica fechado. */
  blocked?: boolean
  /** Aviso do contador regressivo do 429, renderizado pela página. */
  throttleNotice?: React.ReactNode
  /** Atribuição do reCAPTCHA, renderizada pela página. */
  attribution?: React.ReactNode
}

/**
 * Segundo fator de quem já passou pelas credenciais: só o código.
 *
 * Serve aos DOIS métodos, e a diferença é inteiramente de texto. O campo é o
 * mesmo porque o código é o mesmo formato nos dois casos — 6 dígitos numéricos,
 * tanto o TOTP quanto o gerado para o e-mail — e o envio é o mesmo
 * `POST /sessions-mfa`.
 *
 * Nada de QR nem de segredo aqui — o segredo sai uma única vez, na vinculação,
 * e a API não o devolve nunca mais. Quem perdeu o app precisa de um reset feito
 * por um administrador.
 */
export function MfaVerifyStep({
  method,
  email,
  timeoutMinutes,
  onSubmit,
  onBack,
  attemptsLeft = null,
  blocked = false,
  throttleNotice = null,
  attribution = null
}: MfaVerifyStepProps): JSX.Element {
  const isMounted = useIsMounted()

  const isEmail = method === 'email'

  /*
   * O e-mail vai como JSX, NUNCA interpolado em string.
   *
   * Aqui isso é automático porque `{email}` em JSX é escapado pelo React. A
   * regra existe porque no `useModal` deste repositório uma `message` do tipo
   * string é injetada com `dangerouslySetInnerHTML`, e já se verificou que um
   * valor com `<img src=x onerror=...>` executa script. Por isso este aviso é
   * um nó da própria tela e não um `modal.info(...)` com template string.
   */
  const emailNotice = isEmail ? (
    <MfaEmailNotice role="status" aria-live="polite">
      <div>
        Enviamos um código de 6 dígitos
        {email ? (
          <>
            {' '}
            para <strong>{email}</strong>
          </>
        ) : null}
        .
        {typeof timeoutMinutes === 'number'
          ? ` Ele vale por ${timeoutMinutes} ${
              timeoutMinutes === 1 ? 'minuto' : 'minutos'
            }.`
          : ''}
      </div>
      {/*
       * Não existe reenvio, e dizê-lo é obrigatório.
       *
       * A API não tem endpoint de resend (a rota antiga
       * `sessions-send-mfa-token` está comentada no `routes`), então um botão
       * "Reenviar código" aqui chamaria o vazio. O único caminho para um código
       * novo é refazer o `POST /sessions`, que descarta os challenges
       * anteriores do usuário — ou seja, invalida o código já enviado. Quem lê
       * isto precisa saber das duas coisas: que tem de voltar, e que o código
       * antigo morre quando o novo nasce.
       */}
      <div className="mt-2">
        Não há reenvio: o código é enviado uma única vez por tentativa de login.
        Se ele não chegou ou já expirou, use <strong>Voltar</strong> e entre com
        e-mail e senha novamente — isso envia um código novo e invalida o
        anterior.
      </div>
    </MfaEmailNotice>
  ) : null

  return (
    <Formik
      initialValues={{ code: '' }}
      validationSchema={codeSchema}
      onSubmit={async (
        values: CodeValues,
        { setSubmitting, setFieldValue }: FormikHelpers<CodeValues>
      ) => {
        try {
          await onSubmit(values.code)
        } catch (error) {
          // Limpa o campo para que o usuário não reenvie o mesmo código. No
          // modo TOTP um código já usado volta como MFA_CODE_REPLAYED e gasta
          // outra tentativa; no modo e-mail esse erro não existe (o código é de
          // uso único e morre com o challenge), mas reenviar o mesmo código
          // recusado continua sendo desperdiçar tentativa.
          if (isMounted()) {
            setFieldValue('code', '')
          }
        } finally {
          // Só faz sentido se o passo continuar na tela; ver useIsMounted.
          if (isMounted()) {
            setSubmitting(false)
          }
        }
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        isSubmitting,
        errors,
        touched
      }) => (
        <Box onSubmit={handleSubmit}>
          <div>
            <LogoBox>
              {isEmail ? 'Código enviado por e-mail' : 'Código do autenticador'}
            </LogoBox>
            <ContentInput>
              <Form.Group className="mb-0">
                <Form.Label>Código de 6 dígitos</Form.Label>
                <Form.Control
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.code}
                  className="input-radius"
                  isInvalid={!!(errors.code && touched.code)}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.code && touched.code && errors.code}
                </Form.Control.Feedback>
                <Form.Text className="text-muted text-left mt-1">
                  {isEmail
                    ? 'Digite o código que enviamos para o seu e-mail.'
                    : 'Abra o app autenticador e informe o código atual.'}
                </Form.Text>
              </Form.Group>

              {emailNotice}

              {typeof attemptsLeft === 'number' && attemptsLeft > 0 && (
                <Form.Text
                  className="text-danger text-left"
                  role="status"
                  aria-live="polite"
                >
                  {attemptsLeft === 1
                    ? '1 tentativa restante antes de reiniciar o login'
                    : `${attemptsLeft} tentativas restantes antes de reiniciar o login`}
                </Form.Text>
              )}

              {throttleNotice}

              <Button
                disabled={isSubmitting || blocked}
                type="submit"
                className="d-block mt-3"
                style={{ width: '100%' }}
                variant="primary"
                block
              >
                Entrar
              </Button>
              <Button
                type="button"
                className="mt-2"
                style={{ width: '100%' }}
                variant="link"
                onClick={onBack}
              >
                Voltar
              </Button>
              {attribution}
            </ContentInput>
          </div>
        </Box>
      )}
    </Formik>
  )
}
